import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  Check,
  Globe,
  Monitor,
  Building2,
  ShieldAlert,
  Palette,
  AlertTriangle,
} from "lucide-react";

import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { WORD_ADDIN_INSTALL_GUIDE } from "@/lib/content";

function GuideStepItem({
  step,
  index,
  sectionId,
}: {
  step: string;
  index: number;
  sectionId: string;
}) {
  return (
    <li
      className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed pl-1"
      data-testid={`${sectionId}-step-${index + 1}`}
    >
      <span className="marker:font-semibold text-indigo-600 dark:text-indigo-400">
        {step}
      </span>
    </li>
  );
}

function MockupFrame({
  title,
  caption,
  children,
  testId,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <figure
      className="mt-6 rounded-2xl border border-mist bg-white dark:bg-card overflow-hidden shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-mist bg-slate-50 dark:bg-navy/40">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </div>
        <div className="ml-3 text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate">
          {title}
        </div>
      </div>
      <div className="p-5 bg-white dark:bg-card">{children}</div>
      <figcaption className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400 border-t border-mist bg-slate-50/60 dark:bg-navy/20">
        Illustration · {caption}
      </figcaption>
    </figure>
  );
}

function WebSideloadMockup() {
  return (
    <MockupFrame
      title="Word for the web — Add-ins"
      caption="The Office Add-ins dialog reached from Home → Add-ins → More Add-ins."
      testId="word-addin-install-mockup-web-sideload"
    >
      <div className="rounded-lg border border-mist overflow-hidden">
        <div className="flex border-b border-mist text-[11px] font-semibold text-slate-500 bg-slate-50">
          <div className="px-3 py-2 text-slate-400">STORE</div>
          <div className="px-3 py-2 border-b-2 border-indigo-500 -mb-px text-indigo-600">
            MY ADD-INS
          </div>
          <div className="px-3 py-2 text-slate-400">ADMIN MANAGED</div>
        </div>
        <div className="p-4 space-y-3 text-[12px]">
          <div className="flex items-center justify-between">
            <span className="text-slate-700 font-medium">Custom add-ins</span>
            <span className="rounded bg-indigo-600 text-white px-2.5 py-1 text-[11px] font-semibold">
              Upload My Add-in
            </span>
          </div>
          <div className="rounded border border-dashed border-mist px-3 py-3 flex items-center gap-2 text-slate-600">
            <span className="h-5 w-5 rounded bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
              X
            </span>
            <span className="font-mono text-[11px] truncate">manifest.xml</span>
            <span className="ml-auto text-indigo-600 font-semibold text-[11px]">
              Browse…
            </span>
          </div>
          <div className="text-[11px] text-slate-500">
            Once uploaded, the ClauseArk add-in appears in My Add-ins for this account.
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

function DesktopTrustedCatalogMockup() {
  return (
    <MockupFrame
      title="Word — Trust Center → Trusted Add-in Catalogs"
      caption="Where IT pins a manifest catalog so the add-in shows up under Insert → My Add-ins → Shared Folder."
      testId="word-addin-install-mockup-desktop-trusted-catalog"
    >
      <div className="grid grid-cols-3 gap-3 text-[11px]">
        <div className="col-span-1 rounded border border-mist bg-slate-50 p-2 space-y-1">
          <div className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
            Trust Center
          </div>
          <div className="text-slate-400">Trusted Publishers</div>
          <div className="text-slate-400">Trusted Locations</div>
          <div className="rounded bg-white border border-indigo-200 px-1.5 py-1 text-indigo-700 font-semibold">
            Trusted Add-in Catalogs
          </div>
          <div className="text-slate-400">Add-ins</div>
        </div>
        <div className="col-span-2 rounded border border-mist p-3 space-y-2">
          <div className="text-slate-700 font-semibold text-[12px]">
            Trusted Catalogs Table
          </div>
          <div className="rounded border border-mist overflow-hidden">
            <div className="grid grid-cols-[1fr_auto] text-[10px] font-semibold text-slate-500 bg-slate-50 px-2 py-1.5 border-b border-mist">
              <div>Catalog URL</div>
              <div>Show in Menu</div>
            </div>
            <div className="grid grid-cols-[1fr_auto] text-[11px] px-2 py-1.5 items-center">
              <div className="font-mono text-slate-700 truncate">
                https://addin.clauseark.com/catalog
              </div>
              <div className="text-emerald-600 font-semibold">✓</div>
            </div>
          </div>
          <div className="text-[10px] text-slate-500">
            Restart Word for the catalog to register.
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

function CentralizedDeploymentMockup() {
  return (
    <MockupFrame
      title="Microsoft 365 admin center — Integrated apps"
      caption="The Deploy Add-in flow used to push ClauseArk to a security group or the entire tenant."
      testId="word-addin-install-mockup-centralized-deployment"
    >
      <div className="space-y-3 text-[11px]">
        <div className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
          Settings → Integrated apps → Deploy Add-in
        </div>
        <div className="rounded border border-mist p-3 space-y-2.5">
          <div>
            <div className="text-slate-500 mb-1 font-semibold">
              Add-in source
            </div>
            <div className="rounded border border-indigo-200 bg-indigo-50/40 px-2 py-1.5 text-indigo-700 font-semibold">
              ◉ Provide link to manifest file
            </div>
          </div>
          <div>
            <div className="text-slate-500 mb-1 font-semibold">
              Manifest URL
            </div>
            <div className="rounded border border-mist px-2 py-1.5 font-mono text-slate-700 truncate">
              https://addin.clauseark.com/manifest.xml
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded border border-mist px-2 py-1.5 text-center text-slate-400">
              Just me
            </div>
            <div className="rounded border border-indigo-300 bg-indigo-50 px-2 py-1.5 text-center text-indigo-700 font-semibold">
              Specific users / groups
            </div>
            <div className="rounded border border-mist px-2 py-1.5 text-center text-slate-400">
              Everyone
            </div>
          </div>
          <div className="flex justify-end">
            <span className="rounded bg-indigo-600 text-white px-3 py-1.5 text-[11px] font-semibold">
              Deploy
            </span>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

const SECTION_NAV: ReadonlyArray<{ id: string; label: string }> = [
  { id: "prerequisites", label: "Prerequisites" },
  { id: "manifest", label: "Manifest URL" },
  { id: "sideload-web", label: "Word for the web" },
  { id: "sideload-desktop", label: "Word for Mac & Windows" },
  { id: "centralized-deployment", label: "Centralized deployment" },
  { id: "sign-in-troubleshooting", label: "Sign-in troubleshooting" },
  { id: "theme-and-compatibility", label: "Theme & compatibility" },
  { id: "next-steps", label: "Next steps" },
];

export default function WordAddinInstallPage() {
  const guide = WORD_ADDIN_INSTALL_GUIDE;
  const [copied, setCopied] = useState(false);

  async function copyManifestUrl() {
    try {
      await navigator.clipboard.writeText(guide.manifest.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Layout>
      <SEOHead title={guide.seo.title} description={guide.seo.description} />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <Link
              href="/resources/docs"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-6 transition-colors"
              data-testid="word-addin-install-back-to-docs"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to documentation
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {guide.hero.eyebrow}
            </div>
            <h1 className="text-display mb-5 text-navy dark:text-white">
              {guide.hero.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 mb-4">
              {guide.hero.subtitle}
            </p>
            <p className="text-sm text-muted-foreground">{guide.hero.estimatedTime}</p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
            {/* Sticky on-page nav */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">
                  On this page
                </div>
                <nav>
                  <ul className="space-y-2">
                    {SECTION_NAV.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-16">
              {/* Prerequisites */}
              <section
                id="prerequisites"
                className="scroll-mt-24"
                data-testid="section-prerequisites"
              >
                <h2 className="text-h2 text-navy dark:text-white mb-3">
                  {guide.prerequisites.title}
                </h2>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.prerequisites.body}
                </p>
                <ul className="space-y-3">
                  {guide.prerequisites.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Manifest URL */}
              <section
                id="manifest"
                className="scroll-mt-24"
                data-testid="section-manifest"
              >
                <h2 className="text-h2 text-navy dark:text-white mb-3">
                  {guide.manifest.label}
                </h2>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-5 leading-relaxed">
                  {guide.manifest.note}
                </p>
                <div className="rounded-xl border border-mist bg-slate-50 dark:bg-navy/40 p-4 flex items-center gap-3">
                  <code
                    className="flex-1 text-sm font-mono text-navy dark:text-slate-100 break-all"
                    data-testid="word-addin-install-manifest-url"
                  >
                    {guide.manifest.url}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyManifestUrl}
                    className="shrink-0"
                    data-testid="word-addin-install-copy-manifest"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1.5" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </section>

              {/* Sideload — Word for the web */}
              <section
                id="sideload-web"
                className="scroll-mt-24"
                data-testid="section-sideload-web"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Globe className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.sideloadWeb.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.sideloadWeb.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400">
                  {guide.sideloadWeb.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="sideload-web"
                    />
                  ))}
                </ol>
                <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-900/30 p-4 flex gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
                    {guide.sideloadWeb.tip}
                  </p>
                </div>
                <WebSideloadMockup />
              </section>

              {/* Sideload — Mac & Windows */}
              <section
                id="sideload-desktop"
                className="scroll-mt-24"
                data-testid="section-sideload-desktop"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <Monitor className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.sideloadDesktop.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.sideloadDesktop.intro}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-mist bg-white dark:bg-card p-6">
                    <h3 className="text-h4 text-navy dark:text-white mb-4">
                      Word for Mac
                    </h3>
                    <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400">
                      {guide.sideloadDesktop.macSteps.map((step, i) => (
                        <GuideStepItem
                          key={step}
                          step={step}
                          index={i}
                          sectionId="sideload-desktop-mac"
                        />
                      ))}
                    </ol>
                  </div>
                  <div className="rounded-2xl border border-mist bg-white dark:bg-card p-6">
                    <h3 className="text-h4 text-navy dark:text-white mb-4">
                      Word for Windows
                    </h3>
                    <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400">
                      {guide.sideloadDesktop.windowsSteps.map((step, i) => (
                        <GuideStepItem
                          key={step}
                          step={step}
                          index={i}
                          sectionId="sideload-desktop-windows"
                        />
                      ))}
                    </ol>
                  </div>
                </div>

                <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-900/30 p-4 flex gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
                    {guide.sideloadDesktop.tip}
                  </p>
                </div>
                <DesktopTrustedCatalogMockup />
              </section>

              {/* Centralized deployment */}
              <section
                id="centralized-deployment"
                className="scroll-mt-24"
                data-testid="section-centralized-deployment"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.centralizedDeployment.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.centralizedDeployment.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.centralizedDeployment.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="centralized-deployment"
                    />
                  ))}
                </ol>
                <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-6">
                  <h3 className="text-sm font-semibold text-navy dark:text-white mb-3">
                    Good to know
                  </h3>
                  <ul className="space-y-2.5">
                    {guide.centralizedDeployment.callouts.map((callout) => (
                      <li
                        key={callout}
                        className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                      >
                        <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{callout}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <CentralizedDeploymentMockup />
              </section>

              {/* Sign-in troubleshooting */}
              <section
                id="sign-in-troubleshooting"
                className="scroll-mt-24"
                data-testid="section-sign-in-troubleshooting"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                    <ShieldAlert className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.signInTroubleshooting.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.signInTroubleshooting.body}
                </p>
                <div className="space-y-4">
                  {guide.signInTroubleshooting.items.map((item) => (
                    <div
                      key={item.symptom}
                      className="rounded-2xl border border-mist bg-white dark:bg-card p-5"
                    >
                      <div className="text-sm font-semibold text-navy dark:text-white mb-2">
                        {item.symptom}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400 mr-1.5">
                          Fix:
                        </span>
                        {item.fix}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Theme & compatibility */}
              <section
                id="theme-and-compatibility"
                className="scroll-mt-24"
                data-testid="section-theme-and-compatibility"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <Palette className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.themeAndCompatibility.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.themeAndCompatibility.body}
                </p>
                <ul className="space-y-3">
                  {guide.themeAndCompatibility.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <CheckCircle2 className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Next steps */}
              <section
                id="next-steps"
                className="scroll-mt-24"
                data-testid="section-next-steps"
              >
                <h2 className="text-h2 text-navy dark:text-white mb-6">
                  {guide.nextSteps.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {guide.nextSteps.items.map((item) => (
                    <Link
                      key={item.path}
                      href={item.path}
                      className="block rounded-2xl border border-mist bg-white dark:bg-card p-6 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
                      data-testid={`word-addin-install-next-step-${item.path.replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-navy dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {item.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
